// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, ToolCase, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { DataTable } from "@/components/data-table";
import { StorePlusIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { CopyableKey } from "@/components/shared/copyable-key";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterPresets } from "@/components/shared/filter-presets";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { KeyChip } from "@/components/shared/key-chip";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { TimezoneCell } from "@/components/shared/timezone-cell";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BulkCreateStoresDialog } from "@/features/stores/bulk-create-stores-dialog";
import { CompareStoreDialog } from "@/features/stores/compare-stores-dialog";
import { CreateStoreDialog } from "@/features/stores/create-store-dialog";
import { StoreActionsMenu } from "@/features/stores/store-actions-menu";
import { ToggleStoreActiveDialog } from "@/features/stores/toggle-store-active-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/stores/index";

interface StoreRow {
  storeId: number;
  storeName: string;
  legibleName: string;
  storeCode?: string;
  chainId: number;
  chainName: string;
  chainLegibleName: string;
  timeZone: string;
  openTime?: string;
  closeTime?: string;
  goLiveDate?: string;
  regionName?: string;
  isActive: boolean;
}

type StoresSortBy =
  | "storeId"
  | "storeName"
  | "legibleName"
  | "chainName"
  | "regionName"
  | "timeZone"
  | "openTime"
  | "closeTime"
  | "goLiveDate";

// `open_time` / `close_time` are `HH:MM:SS` times of day, not instants, so they're sliced to
// `HH:MM` rather than run through the Date-based time helpers.
function clockTime(value: string | undefined): string {
  return value ? value.slice(0, 5) : "";
}

const FILTER_DEFAULTS = {
  search: "",
  onlyActive: true,
  chain: "",
  region: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "legibleName",
  sortDesc: false,
};

/** A store's own open/close column, matching the monospace digits of the date cells beside it. */
function ClockCell({ value }: { value: string | undefined }) {
  const time = clockTime(value);
  if (!time) return <span className="text-muted-foreground">—</span>;
  return <span className="font-mono tabular-nums">{time}</span>;
}

export function StoresList() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.storesEdit);
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [duplicateFrom, setDuplicateFrom] = useState<{ copyFrom: string; chainName: string; timeZone: string }>();
  const [toggleStore, setToggleStore] = useState<StoreRow | null>(null);
  const [compareDialogStore, setCompareDialogStore] = useState<string | null>(null);

  const sortBy = (sorting[0]?.id ?? "legibleName") as StoresSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);
  const [chainFilter, setChainFilter] = useState(initial.chain);
  const [regionFilter, setRegionFilter] = useState(initial.region);

  const availableRegions = useMemo(() => {
    if (!chainFilter) return [];
    return context?.regionsByChain?.[chainFilter] ?? [];
  }, [chainFilter, context?.regionsByChain]);

  const isFiltered = !!(search || chainFilter || regionFilter || !onlyActive);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setChainFilter("");
    setRegionFilter("");
    setOnlyActive(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({
    isFiltered,
    clearFilters,
    disabled: createDialogOpen || compareDialogStore !== null || toggleStore !== null,
  });

  // The single description of "what is filtered right now" — shared by the URL sync and the presets.
  const currentFilters = useMemo(
    () => ({
      search,
      onlyActive,
      chain: chainFilter,
      region: regionFilter,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id ?? "legibleName",
      sortDesc: sorting[0]?.desc ?? false,
    }),
    [search, onlyActive, chainFilter, regionFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const encoded = encodeFilters({ ...currentFilters, ...overrides }, FILTER_DEFAULTS);
      navigate({
        to: "/stores",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [currentFilters, navigate],
  );

  const applyPreset = useCallback(function applyPreset(next: typeof FILTER_DEFAULTS) {
    setSearch(next.search);
    setOnlyActive(next.onlyActive);
    setChainFilter(next.chain);
    setRegionFilter(next.region);
    setSorting([{ id: next.sortId, desc: next.sortDesc }]);
    // A preset carries no page index, and page size stays the viewer's own preference.
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const { data, isLoading, refetch, isFetching } = useStoresQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyActive: onlyActive || undefined,
    chainName: chainFilter || undefined,
    regionName: regionFilter || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/stores", {
      params: {
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[chainName]": chainFilter || undefined,
          "filter[regionName]": regionFilter || undefined,
          "filter[onlyActive]": onlyActive || undefined,
        },
      },
    });
    const result = res.data as unknown as {
      stores: Array<{
        storeId: number;
        storeName: string;
        legibleName: string;
        chainId: number;
        timeZone: string;
        openTime?: string;
        closeTime?: string;
        goLiveDate?: string;
        regionName?: string;
        isActive: boolean;
      }>;
      chains: Array<{ chainId: number; chainName: string; legibleName: string }>;
    };
    const cMap = new Map(result.chains.map((c) => [c.chainId, c]));
    downloadCsv(
      "stores.csv",
      ["ID", "Name", "Key", "Chain", "Region", "Timezone", "Opens", "Closes", "Go-Live Date", "Status"],
      result.stores.map((s) => [
        String(s.storeId),
        s.legibleName,
        s.storeName,
        cMap.get(s.chainId)?.legibleName ?? "",
        s.regionName ?? "",
        s.timeZone,
        clockTime(s.openTime),
        clockTime(s.closeTime),
        // Already an ISO `yyyy-MM-dd` from the API: emitted raw so the recipient's Excel regional
        // settings can't reinterpret it, and never widened to a timestamp (that would shift the day).
        s.goLiveDate ?? "",
        s.isActive ? "Active" : "Inactive",
      ]),
    );
  }, [search, sortBy, orderBy, chainFilter, regionFilter, onlyActive]);

  const chainMap = useMemo(() => new Map((data?.chains ?? []).map((c) => [c.chainId, c])), [data?.chains]);

  const rows = useMemo<StoreRow[]>(
    () =>
      (data?.stores ?? []).map((store) => {
        const chain = chainMap.get(store.chainId);
        return {
          ...store,
          chainName: chain?.chainName ?? "",
          chainLegibleName: chain?.legibleName ?? "",
        };
      }),
    [data?.stores, chainMap],
  );

  const columns = useMemo<ColumnDef<StoreRow, unknown>[]>(
    () => [
      {
        accessorKey: "storeId",
        header: t("stores.list.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("stores.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.storeId} />,
      },
      {
        accessorKey: "legibleName",
        header: t("stores.list.columns.legibleName"),
        meta: { columnLabel: t("stores.list.columns.legibleName") },
        cell: ({ row, table }) => {
          // Surface the store key as a sub-line when its dedicated column is hidden from the view.
          const keyColumnVisible = table.getColumn("storeName")?.getIsVisible() ?? false;
          return (
            <div className="flex flex-col">
              <StoreLink
                storeName={row.original.storeName}
                legibleName={row.original.legibleName}
                className="font-medium"
                showIcon={false}
              />
              {!keyColumnVisible && <CopyableKey value={row.original.storeName} />}
            </div>
          );
        },
      },
      {
        accessorKey: "storeName",
        header: t("stores.list.columns.storeName"),
        meta: { columnLabel: t("stores.list.columns.storeName") },
        cell: ({ row }) => <KeyChip label={row.original.storeName} variant="store" />,
      },
      {
        accessorKey: "storeCode",
        header: t("stores.list.columns.storeCode"),
        enableSorting: false,
        meta: { columnLabel: t("stores.list.columns.storeCode"), defaultHidden: true },
        cell: ({ row }) =>
          row.original.storeCode ? <span className="font-mono text-sm">{row.original.storeCode}</span> : "—",
      },
      {
        accessorKey: "chainLegibleName",
        header: t("stores.list.columns.chain"),
        enableSorting: false,
        meta: { columnLabel: t("stores.list.columns.chain") },
        cell: ({ row }) =>
          row.original.chainName ? (
            <ChainLink chainName={row.original.chainName} legibleName={row.original.chainLegibleName} />
          ) : (
            "—"
          ),
      },
      {
        accessorKey: "regionName",
        header: t("stores.list.columns.regionName"),
        meta: { columnLabel: t("stores.list.columns.regionName") },
        cell: ({ row }) => row.original.regionName ?? "—",
      },
      {
        accessorKey: "timeZone",
        header: t("stores.list.columns.timezone"),
        enableSorting: true,
        meta: { columnLabel: t("stores.list.columns.timezone") },
        cell: ({ row }) => <TimezoneCell timeZone={row.original.timeZone} />,
      },
      {
        accessorKey: "openTime",
        header: t("stores.list.columns.openTime"),
        size: 90,
        // Pin first-sort direction so the none→asc→desc→none cycle can't skip desc when the first
        // row's value is missing (see the same note on the devices list).
        sortDescFirst: false,
        meta: { columnLabel: t("stores.list.columns.openTime"), defaultHidden: true },
        cell: ({ row }) => <ClockCell value={row.original.openTime} />,
      },
      {
        accessorKey: "closeTime",
        header: t("stores.list.columns.closeTime"),
        size: 90,
        sortDescFirst: false,
        meta: { columnLabel: t("stores.list.columns.closeTime"), defaultHidden: true },
        cell: ({ row }) => <ClockCell value={row.original.closeTime} />,
      },
      {
        accessorKey: "goLiveDate",
        header: t("stores.list.columns.goLiveDate"),
        sortDescFirst: false,
        meta: { columnLabel: t("stores.list.columns.goLiveDate"), defaultHidden: true },
        cell: ({ row }) => <DateValue value={row.original.goLiveDate} />,
      },
      {
        id: "isActive",
        header: t("stores.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("stores.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <StoreActionsMenu
            isActive={row.original.isActive}
            onDuplicate={() => {
              setDuplicateFrom({
                copyFrom: row.original.storeName,
                chainName: row.original.chainName,
                timeZone: row.original.timeZone,
              });
              setCreateDialogOpen(true);
            }}
            onCompareSettings={() => setCompareDialogStore(row.original.storeName)}
            onToggleActive={() => setToggleStore(row.original)}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <div>
      <PageHeader title={t("stores.title")} />
      <div className="p-6">
        <DataTable
          storageKey="stores-list"
          columns={columns}
          data={rows}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={refetch}
          isRefreshing={isFetching}
          onExport={handleExport}
          searchPlaceholder={t("stores.list.searchPlaceholder", "Search stores...")}
          presets={
            <FilterPresets
              storageKey="stores-list"
              defaults={FILTER_DEFAULTS}
              current={currentFilters}
              onApply={applyPreset}
            />
          }
          filters={
            <>
              <FilterSelect
                label={t("stores.list.filter.chain")}
                value={chainFilter}
                onChange={(v) => {
                  setChainFilter(v);
                  setRegionFilter("");
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                searchPlaceholder={t("stores.form.searchChains")}
                emptyText={t("stores.form.noChains")}
                loading={contextLoading}
              />
              {availableRegions.length > 0 && (
                <FilterSelect
                  label={t("stores.list.filter.region")}
                  value={regionFilter}
                  onChange={(v) => {
                    setRegionFilter(v);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  options={availableRegions.map((r) => ({ label: r, value: r }))}
                  searchPlaceholder={t("stores.list.filter.searchRegions")}
                  emptyText={t("stores.list.filter.noRegions")}
                />
              )}
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            canEdit ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label={t("stores.tools.menuLabel")}>
                      <ToolCase className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={-36} className="dropdown-overlap">
                    <DropdownMenuLabel className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-wider text-muted-foreground">
                      <ToolCase className="h-3.5 w-3.5" />
                      {t("stores.tools.menuLabel")}
                    </DropdownMenuLabel>
                    <DropdownMenuItem onSelect={() => setBulkDialogOpen(true)}>
                      {t("stores.tools.bulkCreate")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  onClick={() => {
                    setDuplicateFrom(undefined);
                    setCreateDialogOpen(true);
                  }}
                >
                  <StorePlusIcon className="h-4 w-4" />
                  {t("stores.list.addStoreButton")}
                </Button>
              </>
            ) : null
          }
        />
      </div>

      <CreateStoreDialog
        key={duplicateFrom?.copyFrom ?? "create"}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        duplicateFrom={duplicateFrom}
      />

      <BulkCreateStoresDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen} />

      <CompareStoreDialog
        defaultStoreName={compareDialogStore ?? ""}
        open={compareDialogStore != null}
        onOpenChange={(open) => {
          if (!open) setCompareDialogStore(null);
        }}
      />

      <ToggleStoreActiveDialog store={toggleStore} onClose={() => setToggleStore(null)} />
    </div>
  );
}
